import React, { useState, useEffect, useRef, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import NoteIcon from '@mui/icons-material/Note';
import DeleteIcon from '@mui/icons-material/Delete';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import { useTheme, alpha } from '@mui/material/styles';
import axiosInstance from './axiosInstance';
import { useSnackbar } from '../providers/SnackbarProvider';
import SectionCard from './ui/SectionCard';

/**
 * Aktivitäten-Spalte („das Rückgrat"): Composer → Geplant → Verlauf.
 *
 * - Composer ist der EINZIGE Ort zum Anlegen (Notiz / Aufgabe / E-Mail-Log / Anruf-Log).
 * - „Geplant" = offene Aufgaben (eigenes /tasks/-Modell), Überfälliges rot;
 *   ohne geplante Aufgabe erscheint der Nächste-Aktivität-Hinweis (Pipedrive-Prinzip).
 * - „Verlauf" = Interaktionen + erledigte Aufgaben + Phasen-Chronik (Systemeinträge
 *   aus status_history), nach Monaten gruppiert, Filter als stille Textzeile.
 * - onMeta({lastActivityAt}) meldet die letzte ECHTE Aktivität nach oben
 *   (geloggte Interaktion, erledigte Aufgabe, Phasenwechsel — NICHT bloß geplante
 *   Aufgaben), damit die Detailseite die Inaktivitäts-Warnung zeigen kann.
 */

const COMPOSER_MODES = [
  { key: 'NOTE', label: 'Notiz', icon: <NoteIcon />, placeholder: 'Notiz schreiben …' },
  { key: 'TASK', label: 'Aufgabe', icon: <TaskAltIcon />, placeholder: 'Was ist zu tun? …' },
  { key: 'EMAIL', label: 'E-Mail', icon: <EmailIcon />, placeholder: 'E-Mail protokollieren (Betreff, Kerninhalt) …' },
  { key: 'CALL', label: 'Anruf', icon: <PhoneIcon />, placeholder: 'Anruf protokollieren (Ergebnis, Vereinbarung) …' },
];

const FILTERS = [
  { key: 'ALL', label: 'Alle' },
  { key: 'NOTE', label: 'Notizen' },
  { key: 'TASK', label: 'Aufgaben' },
  { key: 'EMAIL', label: 'E-Mails' },
  { key: 'CALL', label: 'Anrufe' },
  { key: 'MEETING', label: 'Termine' },
  { key: 'SYSTEM', label: 'Änderungen' },
];

const ActivityHub = ({
  relatedType, relatedId, phaseChangedAt, phaseLabel, disabled,
  statusHistory, statusLabels, onMeta,
}) => {
  const theme = useTheme();
  const { showSnackbar } = useSnackbar();
  const inputRef = useRef(null);
  const onMetaRef = useRef(onMeta);
  onMetaRef.current = onMeta;

  const [interactions, setInteractions] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [filter, setFilter] = useState('ALL');
  const [mode, setMode] = useState('NOTE');
  const [text, setText] = useState('');
  const [dueDate, setDueDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showAllHistory, setShowAllHistory] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [intRes, taskRes] = await Promise.all([
          axiosInstance.get('/interactions/', { params: { related_type: relatedType, related_id: relatedId } }),
          axiosInstance.get('/tasks/', { params: { related_type: relatedType, related_id: relatedId } }),
        ]);
        setInteractions(intRes.data);
        setTasks(taskRes.data);
      } catch (error) {
        console.error('Error loading activities', error);
      } finally {
        setLoaded(true);
      }
    };
    load();
  }, [relatedType, relatedId]);

  const typeColor = (type) => (theme.palette.interaction || {})[String(type).toLowerCase()] || theme.palette.interaction?.note || theme.palette.primary.main;

  const typeIcon = (type) => {
    switch (type) {
      case 'CALL': return <PhoneIcon />;
      case 'EMAIL': return <EmailIcon />;
      case 'TASK': return <TaskAltIcon />;
      case 'MEETING': return <MeetingRoomIcon />;
      case 'SYSTEM': return <SwapHorizIcon />;
      default: return <NoteIcon />;
    }
  };

  // ---------- Anlegen (Composer) ----------
  const submit = async () => {
    const trimmed = text.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      if (mode === 'TASK') {
        const response = await axiosInstance.post('/tasks/', {
          title: trimmed.split('\n')[0].slice(0, 120),
          description: trimmed.includes('\n') ? trimmed : '',
          assignee: null,
          due_date: dueDate,
          _related_type_input: relatedType,
          object_id: relatedId,
        });
        setTasks((prev) => [...prev, response.data]);
        showSnackbar('Aufgabe geplant', 'success');
      } else {
        const firstLine = trimmed.split('\n')[0];
        const response = await axiosInstance.post('/interactions/', {
          type: mode,
          title: firstLine.length > 120 ? `${firstLine.slice(0, 117)}…` : firstLine,
          description: trimmed,
          assignee: null,
          due_date: null,
          related_type: relatedType,
          object_id: relatedId,
        });
        setInteractions((prev) => [response.data, ...prev]);
        showSnackbar('Erfasst', 'success');
      }
      setText('');
      setDueDate(null);
    } catch (error) {
      console.error('Error creating activity:', error);
      showSnackbar('Fehler beim Erfassen', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const planTask = () => {
    setMode('TASK');
    if (inputRef.current) inputRef.current.focus();
  };

  // ---------- Aufgaben (Geplant) ----------
  const openTasks = tasks
    .filter((t) => !t.completed_at)
    .sort((a, b) => {
      if (a.due_date && b.due_date) return dayjs(a.due_date).diff(dayjs(b.due_date));
      if (a.due_date) return -1;
      if (b.due_date) return 1;
      return dayjs(a.created_at).diff(dayjs(b.created_at));
    });

  const isOverdue = (task) => task.due_date && dayjs(task.due_date).isBefore(dayjs(), 'day');

  const toggleTask = async (task) => {
    try {
      const payload = task.completed_at ? { completed_at: null } : { completed_at: new Date().toISOString() };
      const response = await axiosInstance.patch(`/tasks/${task.id}/`, payload);
      setTasks((prev) => prev.map((t) => (t.id === task.id ? response.data : t)));
    } catch (error) {
      console.error('Error toggling task:', error);
      showSnackbar('Fehler beim Aktualisieren der Aufgabe', 'error');
    }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm('Diese Aufgabe wirklich löschen?')) return;
    try {
      await axiosInstance.delete(`/tasks/${taskId}/`);
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
      showSnackbar('Aufgabe gelöscht', 'success');
    } catch (error) {
      console.error('Error deleting task:', error);
      showSnackbar('Fehler beim Löschen', 'error');
    }
  };

  const deleteInteraction = async (interactionId) => {
    if (!window.confirm('Diesen Eintrag wirklich löschen?')) return;
    try {
      await axiosInstance.delete(`/interactions/${interactionId}/`);
      setInteractions((prev) => prev.filter((i) => i.id !== interactionId));
      showSnackbar('Eintrag gelöscht', 'success');
    } catch (error) {
      console.error('Error deleting interaction:', error);
      showSnackbar('Fehler beim Löschen', 'error');
    }
  };

  // Letzte ECHTE Aktivität → nach oben melden (für die Inaktivitäts-Warnung).
  // Geplante, noch offene Aufgaben zählen NICHT (nur erledigte).
  const lastActivityAt = useMemo(() => {
    const times = [];
    interactions.forEach((i) => times.push(i.created_at));
    tasks.forEach((t) => { if (t.completed_at) times.push(t.completed_at); });
    (statusHistory || []).forEach((h) => times.push(h.at));
    if (phaseChangedAt) times.push(phaseChangedAt);
    const ms = times.filter(Boolean).map((d) => new Date(d).getTime()).filter((n) => !isNaN(n));
    return ms.length ? new Date(Math.max(...ms)).toISOString() : null;
  }, [interactions, tasks, statusHistory, phaseChangedAt]);

  useEffect(() => {
    if (loaded && onMetaRef.current) onMetaRef.current({ lastActivityAt });
  }, [lastActivityAt, loaded]);

  // ---------- Verlauf ----------
  const phaseEntries = (statusHistory && statusHistory.length)
    ? statusHistory.map((h, idx) => ({ kind: 'system', type: 'SYSTEM', date: h.at, key: `ph-${idx}-${h.at}`, data: { from: h.from, to: h.to } }))
    : (phaseChangedAt ? [{ kind: 'system', type: 'SYSTEM', date: phaseChangedAt, key: 'ph-current', data: { phaseLabel } }] : []);

  const historyItems = [
    ...interactions.map((i) => ({ kind: 'interaction', type: i.type, date: i.created_at, data: i })),
    ...tasks.filter((t) => t.completed_at).map((t) => ({ kind: 'taskDone', type: 'TASK', date: t.completed_at, data: t })),
    ...phaseEntries,
  ].sort((a, b) => dayjs(b.date).diff(dayjs(a.date)));

  const statusLabelFor = (k) => (k == null ? null : (statusLabels && statusLabels[k]) || k);

  const filtered = historyItems.filter((item) => {
    if (filter === 'ALL') return true;
    if (filter === 'SYSTEM') return item.kind === 'system';
    if (filter === 'TASK') return item.type === 'TASK';
    return item.kind === 'interaction' && item.type === filter;
  });

  // Lange Historien gedeckelt — „Gesamten Verlauf anzeigen" klappt auf
  const HISTORY_LIMIT = 8;
  const visibleItems = showAllHistory ? filtered : filtered.slice(0, HISTORY_LIMIT);

  const groups = [];
  visibleItems.forEach((item) => {
    const label = dayjs(item.date).locale('de').format('MMMM YYYY');
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(item);
    else groups.push({ label, items: [item] });
  });

  const relDate = (dateStr) => {
    const diff = dayjs().startOf('day').diff(dayjs(dateStr).startOf('day'), 'day');
    if (diff === 0) return 'Heute';
    if (diff === 1) return 'Gestern';
    return dayjs(dateStr).format('DD.MM.');
  };

  const overline = (label) => (
    <Typography sx={{ fontFamily: 'monospace', fontSize: '0.64rem', letterSpacing: '.14em', textTransform: 'uppercase', color: 'text.secondary', fontWeight: 700 }}>
      {label}
    </Typography>
  );

  const iconBubble = (type, muted = false) => (
    <Box sx={{
      width: 26, height: 26, borderRadius: '50%', flex: 'none', mt: 0.2,
      bgcolor: muted ? 'action.hover' : alpha(typeColor(type), 0.14),
      color: muted ? 'text.secondary' : typeColor(type),
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {React.cloneElement(typeIcon(type), { sx: { fontSize: 14 } })}
    </Box>
  );

  const hoverRow = {
    display: 'flex', gap: 1.25, py: 0.9, px: 1, mx: -1, borderRadius: '8px', alignItems: 'flex-start',
    '&:hover': { bgcolor: 'action.hover' },
    '&:hover .row-actions': { opacity: 1 },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, minWidth: 0 }}>
      {/* ---------- Composer ---------- */}
      <SectionCard>
        {/* Typ-Wahl als Unterstrich-Tabs (v2.4, nach User-Mockup) */}
        <Box sx={{ display: 'flex', gap: 2.5, mb: 1.5, borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
          {COMPOSER_MODES.map((m) => {
            const active = mode === m.key;
            const color = typeColor(m.key);
            return (
              <Box
                key={m.key} role="button" tabIndex={0}
                onClick={() => setMode(m.key)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setMode(m.key); } }}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.6, pb: 0.9, mb: '-1px',
                  fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', userSelect: 'none',
                  color: active ? color : 'text.secondary',
                  borderBottom: '2px solid', borderColor: active ? color : 'transparent',
                  transition: 'color .15s, border-color .15s',
                  '&:hover': { color },
                  '&:focus-visible': { outline: `2px solid ${theme.palette.secondary.main}`, outlineOffset: 2, borderRadius: 1 },
                }}
              >
                {React.cloneElement(m.icon, { sx: { fontSize: 16 } })}
                {m.label}
              </Box>
            );
          })}
        </Box>
        <TextField
          inputRef={inputRef}
          fullWidth multiline minRows={2} size="small"
          placeholder={COMPOSER_MODES.find((m) => m.key === mode)?.placeholder}
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={disabled}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1, flexWrap: 'wrap' }}>
          {mode === 'TASK' && (
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
              <DatePicker
                label="Fällig am"
                value={dueDate ? dayjs(dueDate) : null}
                onChange={(v) => setDueDate(v && dayjs(v).isValid() ? v.toISOString() : null)}
                slotProps={{ field: { clearable: true }, textField: { size: 'small', sx: { width: 190 } } }}
              />
            </LocalizationProvider>
          )}
          <Button variant="contained" size="small" sx={{ ml: 'auto' }} onClick={submit} disabled={disabled || submitting || !text.trim()}>
            {mode === 'TASK' ? 'Aufgabe planen' : 'Erfassen'}
          </Button>
        </Box>
      </SectionCard>

      {/* ---------- Geplant ---------- */}
      <SectionCard>
        {overline('Geplant')}
        {openTasks.map((task) => {
          const overdue = isOverdue(task);
          return (
            <Box key={task.id} sx={hoverRow}>
              <Checkbox
                checked={false} size="small" disabled={disabled}
                onChange={() => toggleTask(task)}
                sx={{ p: 0.25, mt: 0, flexShrink: 0, '&.Mui-checked': { color: 'success.main' } }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{task.title}</Typography>
                {task.description && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>{task.description}</Typography>
                )}
                <Typography variant="caption" sx={{ color: overdue ? 'error.main' : 'text.secondary', fontWeight: overdue ? 700 : 400 }}>
                  {task.due_date ? `Fällig ${dayjs(task.due_date).format('DD.MM.YY')}${overdue ? ' · überfällig' : ''}` : 'Ohne Termin'}
                  {task.assignee_name ? ` · ${task.assignee_name}` : ''}
                </Typography>
              </Box>
              <Box className="row-actions" sx={{ opacity: 0, transition: 'opacity .15s', flexShrink: 0 }}>
                <Tooltip title="Löschen">
                  <IconButton size="small" onClick={() => deleteTask(task.id)}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>
                </Tooltip>
              </Box>
            </Box>
          );
        })}
        {loaded && openTasks.length === 0 && !disabled && (
          <Box sx={{
            mt: 1, px: 1.5, py: 1, borderRadius: '10px',
            border: '1px dashed', borderColor: alpha(theme.palette.warning.main, 0.5),
            bgcolor: alpha(theme.palette.warning.main, 0.07),
            display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
          }}>
            <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 600 }}>
              Keine nächste Aktivität geplant.
            </Typography>
            <Button size="small" sx={{ ml: 'auto', fontSize: '0.75rem', color: 'warning.main' }} onClick={planTask}>
              + Aufgabe planen
            </Button>
          </Box>
        )}
        {loaded && openTasks.length === 0 && disabled && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>Keine offenen Aufgaben.</Typography>
        )}
      </SectionCard>

      {/* ---------- Verlauf ---------- */}
      <SectionCard>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
          {overline('Verlauf')}
          <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'wrap', ml: 'auto' }}>
            {FILTERS.map((f) => {
              const active = filter === f.key;
              return (
                <Box
                  key={f.key} role="button" tabIndex={0}
                  onClick={() => setFilter(f.key)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFilter(f.key); } }}
                  sx={{
                    fontSize: '0.74rem', fontWeight: 600, px: active ? 1 : 0.5, py: 0.2, borderRadius: 999,
                    cursor: 'pointer', userSelect: 'none',
                    color: active ? 'primary.main' : 'text.disabled',
                    bgcolor: active ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                    '&:hover': { color: active ? 'primary.main' : 'text.secondary' },
                    '&:focus-visible': { outline: `2px solid ${theme.palette.secondary.main}`, outlineOffset: 1 },
                  }}
                >
                  {f.label}
                </Box>
              );
            })}
          </Box>
        </Box>

        {loaded && filtered.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            {filter === 'ALL'
              ? 'Noch keine Einträge — halte Notizen, E-Mails und Anrufe oben im Eingabefeld fest.'
              : 'Keine Einträge für diesen Filter.'}
          </Typography>
        )}

        {groups.map((group) => (
          <Box key={group.label} sx={{ mt: 1.5 }}>
            <Typography component="span" sx={{
              display: 'inline-block', fontFamily: 'monospace', fontSize: '0.62rem', letterSpacing: '.12em',
              textTransform: 'uppercase', color: 'text.secondary', fontWeight: 700, mb: 0.5,
              bgcolor: 'action.hover', px: 1, py: 0.25, borderRadius: 999,
            }}>
              {group.label}
            </Typography>
            {group.items.map((item) => {
              if (item.kind === 'system') {
                const d = item.data;
                const isFallback = d.from === undefined && d.to === undefined;
                const title = d.from == null && !isFallback ? 'Erstellt' : 'Phase geändert';
                const sub = isFallback
                  ? `Aktuelle Phase: ${d.phaseLabel}`
                  : d.from == null
                    ? `Startphase: ${statusLabelFor(d.to)}`
                    : `${statusLabelFor(d.from)} → ${statusLabelFor(d.to)}`;
                return (
                  <Box key={item.key} sx={{ ...hoverRow, '&:hover': {} }}>
                    {iconBubble('SYSTEM', true)}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{title}</Typography>
                      <Typography variant="caption" color="text.secondary">{sub}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>{relDate(item.date)}</Typography>
                  </Box>
                );
              }
              if (item.kind === 'taskDone') {
                const t = item.data;
                return (
                  <Box key={`task-${t.id}`} sx={hoverRow}>
                    {iconBubble('TASK')}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, textDecoration: 'line-through', color: 'text.secondary' }}>{t.title}</Typography>
                      <Typography variant="caption" sx={{ color: 'success.main' }}>
                        Erledigt {dayjs(t.completed_at).format('DD.MM.YY')}{t.assignee_name ? ` · ${t.assignee_name}` : ''}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                      <Box className="row-actions" sx={{ opacity: 0, transition: 'opacity .15s' }}>
                        <Tooltip title="Wieder öffnen">
                          <Checkbox checked size="small" disabled={disabled} onChange={() => toggleTask(t)} sx={{ p: 0.25, color: 'success.main', '&.Mui-checked': { color: 'success.main' } }} />
                        </Tooltip>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{relDate(item.date)}</Typography>
                    </Box>
                  </Box>
                );
              }
              const i = item.data;
              return (
                <Box key={`int-${i.id}`} sx={hoverRow}>
                  {iconBubble(i.type)}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{i.title}</Typography>
                    {i.description && i.description !== i.title && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>{i.description}</Typography>
                    )}
                    {i.assignee_name && (
                      <Typography variant="caption" color="text.secondary">{i.assignee_name}</Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    <Box className="row-actions" sx={{ opacity: 0, transition: 'opacity .15s' }}>
                      <Tooltip title="Löschen">
                        <IconButton size="small" onClick={() => deleteInteraction(i.id)}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>
                      </Tooltip>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{relDate(item.date)}</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        ))}

        {!showAllHistory && filtered.length > HISTORY_LIMIT && (
          <Button fullWidth size="small" variant="outlined" sx={{ mt: 2 }} onClick={() => setShowAllHistory(true)}>
            Gesamten Verlauf anzeigen ({filtered.length})
          </Button>
        )}
      </SectionCard>
    </Box>
  );
};

export default ActivityHub;
